import { useState, useEffect, useMemo } from "react";
import { format, subDays, startOfMonth, startOfDay, endOfDay } from "date-fns";
import { es } from "date-fns/locale";
import { TrendingUp, TrendingDown, Store, CreditCard, Package, Euro, FileDown, RefreshCw, Receipt, RotateCcw, AlertTriangle, Smartphone, Users } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from "recharts";

type TimeFilter = "today" | "week" | "month";

interface GrowthStats {
  revenueGrowth: number;
  ordersGrowth: number;
  previousPeriod: {
    sales: number;
    orders: number;
  };
}

interface StatsData {
  totalSales: number;
  orderCount: number;
  avgTicket: number;
  salesByLocation: { name: string; total: number }[];
  topProducts: { name: string; quantity: number }[];
  paymentMethods: { name: string; value: number }[];
  salesByHour: { hour: string; sales: number; orders: number }[];
  salesBySource: { name: string; value: number; sales: number }[];
  growthStats: GrowthStats;
}

interface AdminStatsProps {
  token: string;
  selectedLocationId: string | null;
  isSuperAdmin?: boolean;
}

const PIE_COLORS = ["#8B4513", "#D4A574", "#C9A86C", "#6B4423"];
const SOURCE_COLORS = ["#22c55e", "#8B4513"];

const AdminStats = ({ token, selectedLocationId, isSuperAdmin = false }: AdminStatsProps) => {
  const { toast } = useToast();
  const [timeFilter, setTimeFilter] = useState<TimeFilter>("today");
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [statsData, setStatsData] = useState<StatsData>({
    totalSales: 0,
    orderCount: 0,
    avgTicket: 0,
    salesByLocation: [],
    topProducts: [],
    paymentMethods: [],
    salesByHour: [],
    salesBySource: [],
    growthStats: {
      revenueGrowth: 0,
      ordersGrowth: 0,
      previousPeriod: { sales: 0, orders: 0 },
    },
  });

  const dateRange = useMemo(() => {
    const now = new Date();
    let start: Date;
    
    switch (timeFilter) {
      case "today":
        start = startOfDay(now);
        break;
      case "week":
        start = subDays(now, 7);
        break;
      case "month":
        start = startOfMonth(now);
        break;
      default:
        start = startOfDay(now);
    }
    
    return { start, end: endOfDay(now) };
  }, [timeFilter]);

  const fetchStats = async () => {
    try {
      setIsLoading(true);
      
      const { data, error } = await supabase.functions.invoke("admin-orders", {
        body: { 
          action: "fetch-stats", 
          token,
          filterLocationId: selectedLocationId,
          startDate: dateRange.start.toISOString(),
          endDate: dateRange.end.toISOString(),
        },
      });

      if (error) throw error;
      if (!data.success) throw new Error(data.error || "Error fetching stats");

      setStatsData({
        ...data.stats,
        avgTicket: data.stats.avgTicket || 0,
        salesByHour: data.stats.salesByHour || [],
        salesBySource: data.stats.salesBySource || [],
        growthStats: data.stats.growthStats || { revenueGrowth: 0, ordersGrowth: 0, previousPeriod: { sales: 0, orders: 0 } },
      });
    } catch (error) {
      console.error("Error fetching stats:", error);
      toast({
        title: "Error",
        description: "No se pudieron cargar las estadísticas",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [timeFilter, selectedLocationId, token]);

  const formatCurrency = (value: number) => {
    return `${value.toFixed(2).replace(".", ",")}€`;
  };

  const getFilterLabel = () => {
    switch (timeFilter) {
      case "today":
        return format(new Date(), "d 'de' MMMM", { locale: es });
      case "week":
        return "Últimos 7 días";
      case "month":
        return format(new Date(), "MMMM yyyy", { locale: es });
    }
  };

  const handleExportCSV = async () => {
    setIsExporting(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-orders", {
        body: {
          action: "export-csv",
          token,
          filterLocationId: selectedLocationId,
          startDate: dateRange.start.toISOString(),
          endDate: dateRange.end.toISOString(),
        },
      });

      if (error) throw error;
      if (!data.success) throw new Error(data.error || "Error exportando datos");

      if (data.data.length === 0) {
        toast({
          title: "Sin datos",
          description: "No hay pedidos en el período seleccionado",
        });
        return;
      }

      // Generate CSV content with Spanish headers
      const headers = [
        "Fecha",
        "Hora", 
        "ID Pedido",
        "Sede",
        "Tipo Pedido",
        "Método Pago",
        "Estado Pago",
        "Base Imponible (€)",
        "IGIC 7% (€)",
        "Total (€)",
        "Estado"
      ];
      
      const csvRows = data.data.map((row: any) => [
        row.fecha,
        row.hora,
        row.id_pedido,
        row.sede,
        row.tipo_pedido,
        row.metodo_pago,
        row.estado_pago,
        row.base_imponible,
        row.igic_7,
        row.total,
        row.estado,
      ].join(";"));

      const csvContent = [headers.join(";"), ...csvRows].join("\n");

      // Create and download file with BOM for Excel compatibility
      const blob = new Blob(["\ufeff" + csvContent], { 
        type: "text/csv;charset=utf-8;" 
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      
      // Filename based on filter
      const filterName = timeFilter === "today" 
        ? format(new Date(), "yyyy-MM-dd")
        : timeFilter === "week"
          ? `semana_${format(new Date(), "yyyy-MM-dd")}`
          : format(new Date(), "yyyy-MM");
      
      link.download = `reporte_ventas_${filterName}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: "Exportación completada",
        description: `${data.data.length} registros exportados correctamente`,
      });
    } catch (error) {
      console.error("Error exporting CSV:", error);
      toast({
        title: "Error",
        description: "No se pudo exportar el reporte",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-pulse text-muted-foreground">Cargando estadísticas...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Time Filter & Export Button */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex gap-2 flex-wrap">
          <Button
            variant={timeFilter === "today" ? "default" : "outline"}
            size="sm"
            onClick={() => setTimeFilter("today")}
          >
            Hoy
          </Button>
          <Button
            variant={timeFilter === "week" ? "default" : "outline"}
            size="sm"
            onClick={() => setTimeFilter("week")}
          >
            Últimos 7 días
          </Button>
          <Button
            variant={timeFilter === "month" ? "default" : "outline"}
            size="sm"
            onClick={() => setTimeFilter("month")}
          >
            Mes actual
          </Button>
        </div>

        {/* Export CSV Button */}
        <Button
          variant="outline"
          size="sm"
          onClick={handleExportCSV}
          disabled={isExporting}
          className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100 dark:bg-green-950/20 dark:border-green-800 dark:text-green-400 dark:hover:bg-green-950/40"
        >
          {isExporting ? (
            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <FileDown className="h-4 w-4 mr-2" />
          )}
          Descargar CSV
        </Button>
      </div>

      <p className="text-sm text-muted-foreground">
        Mostrando datos de: <span className="font-medium text-foreground">{getFilterLabel()}</span>
      </p>

      {/* KPI Cards - 3 columns with growth indicators */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Euro className="h-5 w-5 text-primary" />
              <span className="text-xs sm:text-sm text-muted-foreground">Ventas Totales</span>
            </div>
            <div className="text-xl sm:text-2xl md:text-3xl font-bold text-primary">
              {formatCurrency(statsData.totalSales)}
            </div>
            {/* Growth indicator */}
            <div className={`flex items-center gap-1 mt-1 text-xs ${statsData.growthStats.revenueGrowth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {statsData.growthStats.revenueGrowth >= 0 ? (
                <TrendingUp className="h-3 w-3" />
              ) : (
                <TrendingDown className="h-3 w-3" />
              )}
              <span>{statsData.growthStats.revenueGrowth >= 0 ? '+' : ''}{statsData.growthStats.revenueGrowth.toFixed(1)}% vs periodo anterior</span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-secondary/10 to-secondary/5 border-secondary/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Package className="h-5 w-5 text-secondary" />
              <span className="text-xs sm:text-sm text-muted-foreground">Tickets</span>
            </div>
            <div className="text-xl sm:text-2xl md:text-3xl font-bold text-secondary">
              {statsData.orderCount}
            </div>
            {/* Growth indicator */}
            <div className={`flex items-center gap-1 mt-1 text-xs ${statsData.growthStats.ordersGrowth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {statsData.growthStats.ordersGrowth >= 0 ? (
                <TrendingUp className="h-3 w-3" />
              ) : (
                <TrendingDown className="h-3 w-3" />
              )}
              <span>{statsData.growthStats.ordersGrowth >= 0 ? '+' : ''}{statsData.growthStats.ordersGrowth.toFixed(1)}% vs periodo anterior</span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-100/50 to-amber-50/50 border-amber-200 dark:from-amber-950/20 dark:to-amber-900/10 dark:border-amber-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Receipt className="h-5 w-5 text-amber-700 dark:text-amber-400" />
              <span className="text-xs sm:text-sm text-muted-foreground">Ticket Medio</span>
            </div>
            <div className="text-xl sm:text-2xl md:text-3xl font-bold text-amber-700 dark:text-amber-400">
              {formatCurrency(statsData.avgTicket)}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Periodo anterior: {formatCurrency(statsData.growthStats.previousPeriod.orders > 0 
                ? statsData.growthStats.previousPeriod.sales / statsData.growthStats.previousPeriod.orders 
                : 0)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sales by Hour - Area Chart (Full Width) */}
      {statsData.salesByHour.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Ritmo de Ventas por Hora
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={statsData.salesByHour}>
                  <defs>
                    <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#D4A574" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#D4A574" stopOpacity={0.1}/>
                    </linearGradient>
                  </defs>
                  <XAxis 
                    dataKey="hour" 
                    tick={{ fontSize: 11 }}
                    interval={0}
                    angle={-45}
                    textAnchor="end"
                    height={50}
                  />
                  <YAxis 
                    tickFormatter={(v) => `${v}€`}
                    tick={{ fontSize: 11 }}
                  />
                  <Tooltip 
                    formatter={(value: number, name: string) => {
                      if (name === 'sales') return [`${value.toFixed(2)}€`, 'Ventas'];
                      return [value, 'Tickets'];
                    }}
                    labelFormatter={(label) => `Hora: ${label}`}
                    contentStyle={{ 
                      backgroundColor: "hsl(var(--background))", 
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px"
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="sales" 
                    stroke="#8B4513" 
                    strokeWidth={2}
                    fill="url(#salesGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-4 mt-2 text-xs text-muted-foreground">
              <span>📊 Pico de ventas: {
                statsData.salesByHour.reduce((max, h) => h.sales > max.sales ? h : max, { hour: '-', sales: 0 }).hour
              }</span>
              <span>🎫 Mayor actividad: {
                statsData.salesByHour.reduce((max, h) => h.orders > max.orders ? h : max, { hour: '-', orders: 0 }).hour
              }</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Two column layout: Order Source + Payment Methods */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Order Source Pie Chart */}
        {statsData.salesBySource.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Smartphone className="h-4 w-4" />
                Origen del Pedido
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statsData.salesBySource}
                      cx="50%"
                      cy="50%"
                      innerRadius={35}
                      outerRadius={60}
                      paddingAngle={2}
                      dataKey="value"
                      nameKey="name"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {statsData.salesBySource.map((entry, index) => (
                        <Cell key={`source-${index}`} fill={SOURCE_COLORS[index % SOURCE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value: number, name: string, props: any) => [
                        `${value} pedidos (${props.payload.sales?.toFixed(2) || 0}€)`, 
                        ""
                      ]}
                      contentStyle={{ 
                        backgroundColor: "hsl(var(--background))", 
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px"
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex justify-center gap-6 mt-2">
                {statsData.salesBySource.map((source, index) => (
                  <div key={source.name} className="flex items-center gap-2 text-sm">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: SOURCE_COLORS[index % SOURCE_COLORS.length] }} 
                    />
                    <span>{source.name}: {source.value}</span>
                  </div>
                ))}
              </div>
              {/* Digitalization metric */}
              {statsData.salesBySource.length > 0 && (() => {
                const qrOrders = statsData.salesBySource.find(s => s.name.includes('QR'))?.value || 0;
                const totalOrders = statsData.salesBySource.reduce((sum, s) => sum + s.value, 0);
                const digitalizationRate = totalOrders > 0 ? (qrOrders / totalOrders) * 100 : 0;
                return (
                  <div className="mt-3 p-2 bg-green-50 dark:bg-green-950/20 rounded-lg text-center">
                    <span className="text-xs text-green-700 dark:text-green-400 font-medium">
                      📈 Tasa de Digitalización: {digitalizationRate.toFixed(0)}%
                    </span>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        )}

        {/* Payment Methods Pie Chart */}
        {statsData.paymentMethods.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                Métodos de Pago
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statsData.paymentMethods}
                      cx="50%"
                      cy="50%"
                      innerRadius={35}
                      outerRadius={60}
                      paddingAngle={2}
                      dataKey="value"
                      nameKey="name"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {statsData.paymentMethods.map((entry, index) => (
                        <Cell key={`payment-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value: number) => [`${value} pedidos`, ""]}
                      contentStyle={{ 
                        backgroundColor: "hsl(var(--background))", 
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px"
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex justify-center gap-6 mt-2">
                {statsData.paymentMethods.map((method, index) => (
                  <div key={method.name} className="flex items-center gap-2 text-sm">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: PIE_COLORS[index % PIE_COLORS.length] }} 
                    />
                    <span>{method.name}: {method.value}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Sales by Location Chart */}
      {statsData.salesByLocation.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Store className="h-4 w-4" />
              Ventas por Sede
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={statsData.salesByLocation} layout="vertical">
                  <XAxis type="number" tickFormatter={(v) => `${v}€`} />
                  <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 12 }} />
                  <Tooltip 
                    formatter={(value: number) => [`${value.toFixed(2)}€`, "Ventas"]}
                    contentStyle={{ 
                      backgroundColor: "hsl(var(--background))", 
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px"
                    }}
                  />
                  <Bar 
                    dataKey="total" 
                    fill="#8B4513" 
                    radius={[0, 4, 4, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top Products */}
      {statsData.topProducts.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Top 5 Productos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {statsData.topProducts.map((product, index) => (
                <div key={product.name} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-sm font-bold flex items-center justify-center">
                      {index + 1}
                    </span>
                    <span className="font-medium">{product.name}</span>
                  </div>
                  <span className="text-muted-foreground">{product.quantity} uds</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Danger Zone - Super Admin Only */}
      {isSuperAdmin && (
        <Card className="border-destructive/50 bg-destructive/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-4 w-4" />
              Zona de Peligro - Reseteo de Contadores
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Reinicia los contadores de numeración de tickets por tipo de pedido. Esta acción es irreversible.
            </p>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {/* Reset Mesa */}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="sm"
                    disabled={isResetting}
                    className="gap-2 border-amber-500 text-amber-700 hover:bg-amber-50"
                  >
                    {isResetting ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <RotateCcw className="h-4 w-4" />
                    )}
                    Resetear Mesas
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-amber-500" />
                      ¿Reiniciar contador de MESAS?
                    </AlertDialogTitle>
                    <AlertDialogDescription className="text-left">
                      Esto reiniciará los tickets de <strong>MESA</strong> al <strong>#M-1</strong>.
                      <br /><br />
                      Solo afecta a los pedidos de mesas de tu sede.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={async () => {
                        setIsResetting(true);
                        try {
                          const { data, error } = await supabase.functions.invoke("admin-orders", {
                            body: { action: "reset-counters", token, target: "mesa" },
                          });
                          if (error) throw error;
                          if (!data.success) throw new Error(data.error || "Error al resetear");
                          toast({ title: "✅ Contador de Mesas reiniciado", description: "El próximo será #M-1" });
                        } catch (err) {
                          toast({ title: "Error", description: err instanceof Error ? err.message : "Fallo al resetear", variant: "destructive" });
                        } finally {
                          setIsResetting(false);
                        }
                      }}
                      className="bg-amber-600 text-white hover:bg-amber-700"
                    >
                      Sí, reiniciar Mesas
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              {/* Reset Llevar */}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="sm"
                    disabled={isResetting}
                    className="gap-2 border-blue-500 text-blue-700 hover:bg-blue-50"
                  >
                    {isResetting ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <RotateCcw className="h-4 w-4" />
                    )}
                    Resetear Para Llevar
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-blue-500" />
                      ¿Reiniciar contador de PARA LLEVAR?
                    </AlertDialogTitle>
                    <AlertDialogDescription className="text-left">
                      Esto reiniciará los tickets de <strong>LLEVAR</strong> al <strong>#L-1</strong>.
                      <br /><br />
                      Solo afecta a los pedidos de mostrador/para llevar de tu sede.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={async () => {
                        setIsResetting(true);
                        try {
                          const { data, error } = await supabase.functions.invoke("admin-orders", {
                            body: { action: "reset-counters", token, target: "llevar" },
                          });
                          if (error) throw error;
                          if (!data.success) throw new Error(data.error || "Error al resetear");
                          toast({ title: "✅ Contador de Llevar reiniciado", description: "El próximo será #L-1" });
                        } catch (err) {
                          toast({ title: "Error", description: err instanceof Error ? err.message : "Fallo al resetear", variant: "destructive" });
                        } finally {
                          setIsResetting(false);
                        }
                      }}
                      className="bg-blue-600 text-white hover:bg-blue-700"
                    >
                      Sí, reiniciar Llevar
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              {/* Reset Encargos - CRITICAL */}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button 
                    variant="destructive" 
                    size="sm"
                    disabled={isResetting}
                    className="gap-2 ring-2 ring-destructive font-semibold"
                  >
                    {isResetting ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <RotateCcw className="h-4 w-4" />
                    )}
                    Resetear Encargos
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="border-destructive">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2 text-destructive">
                      <AlertTriangle className="h-6 w-6" />
                      ⚠️ PRECAUCIÓN: Ámbito Global
                    </AlertDialogTitle>
                    <AlertDialogDescription className="text-left space-y-3">
                      <p>
                        Estás a punto de resetear el contador de <strong className="text-destructive">ENCARGOS</strong>.
                      </p>
                      <div className="bg-destructive/10 border border-destructive/30 rounded-md p-3">
                        <p className="font-semibold text-destructive">
                          ⚠️ Esto afecta a TODAS las sedes (Siete Palmas, Lomo Los Frailes, etc.)
                        </p>
                        <p className="text-sm mt-2">
                          Si reinicias esto ahora, el Almacén podría recibir pedidos con numeración 
                          duplicada o saltos extraños (ej: del #342 al #1).
                        </p>
                      </div>
                      <p className="font-medium">
                        Hazlo solo si has coordinado el cierre total con el Almacén.
                      </p>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={async () => {
                        setIsResetting(true);
                        try {
                          const { data, error } = await supabase.functions.invoke("admin-orders", {
                            body: { action: "reset-counters", token, target: "encargo" },
                          });
                          if (error) throw error;
                          if (!data.success) throw new Error(data.error || "Error al resetear");
                          toast({ title: "✅ Contador de Encargos reiniciado", description: "El próximo será #E-1" });
                        } catch (err) {
                          toast({ title: "Error", description: err instanceof Error ? err.message : "Fallo al resetear", variant: "destructive" });
                        } finally {
                          setIsResetting(false);
                        }
                      }}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      Entiendo, reiniciar Encargos
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminStats;
